import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Gift, 
  Users, 
  Copy, 
  CheckCircle2,
  Share2,
  Twitter,
  Facebook,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferralCard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferralStats();
  }, []);

  const fetchReferralStats = async () => {
    try {
      const response = await axios.get(`${API}/referral/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch referral stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    if (stats?.referral_link) {
      navigator.clipboard.writeText(stats.referral_link);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareLinks = stats?.referral_link ? {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent('Protect your privacy from surveillance tools like Fog Reveal. Use my link to get started:')}&url=${encodeURIComponent(stats.referral_link)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(stats.referral_link)}`,
  } : {};

  if (loading) {
    return (
      <Card className="bg-[#0a0a0a] border-[#262626]">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-[#00FF94] animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[#0a0a0a] border-[#262626]" data-testid="referral-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center">
            <Gift className="w-5 h-5 text-[#00FF94]" />
          </div>
          <div>
            <CardTitle className="text-white text-lg">Refer & Earn</CardTitle>
            <CardDescription className="text-gray-500">Get 1 month free for each friend who upgrades</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-3 bg-[#171717] rounded-lg">
            <div className="font-mono text-xl text-white">{stats?.referrals_count || 0}</div>
            <div className="text-xs text-gray-500">Referrals</div>
          </div>
          <div className="text-center p-3 bg-[#171717] rounded-lg">
            <div className="font-mono text-xl text-[#00FF94]">{stats?.successful_referrals || 0}</div>
            <div className="text-xs text-gray-500">Converted</div>
          </div>
          <div className="text-center p-3 bg-[#171717] rounded-lg">
            <div className="font-mono text-xl text-white">{stats?.rewards_earned || 0}</div>
            <div className="text-xs text-gray-500">Months Earned</div>
          </div>
        </div>

        {/* Referral Link */}
        <div>
          <label className="text-xs text-gray-500 mb-2 block">Your Referral Link</label>
          <div className="flex gap-2">
            <Input
              value={stats?.referral_link || 'Loading...'}
              readOnly
              className="bg-[#171717] border-[#262626] text-white text-sm font-mono"
              data-testid="referral-link"
            />
            <Button 
              onClick={copyReferralLink}
              variant="outline"
              className="border-[#262626] text-gray-400 hover:text-white hover:bg-white/5 px-3"
              data-testid="copy-referral"
            >
              {copied ? <CheckCircle2 className="w-4 h-4 text-[#00FF94]" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Share Buttons */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Share:</span>
          <a 
            href={shareLinks.twitter}
            target="_blank"
            rel="noopener noreferrer"
            className="w-8 h-8 rounded-lg bg-[#171717] border border-[#262626] flex items-center justify-center text-gray-400 hover:text-[#1DA1F2] hover:border-[#1DA1F2]/30 transition-colors"
          >
            <Twitter className="w-4 h-4" />
          </a>
          <a 
            href={shareLinks.facebook}
            target="_blank"
            rel="noopener noreferrer"
            className="w-8 h-8 rounded-lg bg-[#171717] border border-[#262626] flex items-center justify-center text-gray-400 hover:text-[#4267B2] hover:border-[#4267B2]/30 transition-colors"
          >
            <Facebook className="w-4 h-4" />
          </a>
        </div>

        {/* Code Display */}
        <div className="text-center p-3 bg-[#00FF94]/5 border border-[#00FF94]/20 rounded-lg">
          <p className="text-xs text-gray-500 mb-1">Your Referral Code</p>
          <p className="font-mono text-lg text-[#00FF94] font-bold tracking-wider">{stats?.code || '...'}</p>
        </div>
      </CardContent>
    </Card>
  );
}
